import { Search, X } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, FormField, FormLabel, FormInput, FormSelect } from '../ui';
import type { LeadFilters as Filters } from '../../types/lead';
import { representativeService } from '../../services/representative.service';
import { leadGroupService } from '../../services/leadGroup.service';

interface LeadFiltersProps {
  onFilter: (filters: Filters) => void;
  initialFilters?: Filters;
}

function LeadFilters({ onFilter, initialFilters }: LeadFiltersProps) {
  const [search, setSearch] = useState(initialFilters?.search || '');
  const [representativeId, setRepresentativeId] = useState<string>(
    initialFilters?.representative_id?.toString() || ''
  );
  const [origin, setOrigin] = useState(initialFilters?.origin || '');
  const [hasEmail, setHasEmail] = useState<string>(
    initialFilters?.has_email !== undefined ? initialFilters.has_email.toString() : ''
  );
  const [hasPhone, setHasPhone] = useState<string>(
    initialFilters?.has_phone !== undefined ? initialFilters.has_phone.toString() : ''
  );
  const [leadGroupId, setLeadGroupId] = useState<string>(
    initialFilters?.lead_group_id?.toString() || ''
  );
  const [lastContactFrom, setLastContactFrom] = useState(initialFilters?.last_contact_from || '');
  const [lastContactTo, setLastContactTo] = useState(initialFilters?.last_contact_to || '');
  const [type, setType] = useState(initialFilters?.type || '');

  const { data: representativesData } = useQuery({
    queryKey: ['representatives-filter'],
    queryFn: () => representativeService.getAll(1, 100),
  });

  const { data: leadGroups } = useQuery({
    queryKey: ['lead-groups'],
    queryFn: () => leadGroupService.getAll(),
  });

  const representatives = representativesData?.data || [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilter({
      search: search || undefined,
      representative_id: representativeId ? parseInt(representativeId) : undefined,
      origin: origin || undefined,
      has_email: hasEmail ? hasEmail === 'true' : undefined,
      has_phone: hasPhone ? hasPhone === 'true' : undefined,
      lead_group_id: leadGroupId ? parseInt(leadGroupId) : undefined,
      last_contact_from: lastContactFrom || undefined,
      last_contact_to: lastContactTo || undefined,
      type: type || undefined,
    });
  };

  const handleClear = () => {
    setSearch('');
    setRepresentativeId('');
    setOrigin('');
    setHasEmail('');
    setHasPhone('');
    setLeadGroupId('');
    setLastContactFrom('');
    setLastContactTo('');
    setType('');
    onFilter({});
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField>
          <FormLabel htmlFor="search">Busca</FormLabel>
          <FormInput
            id="search"
            type="text"
            placeholder="Nome, e-mail ou telefone"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="representative_id">Vendedor</FormLabel>
          <FormSelect
            id="representative_id"
            value={representativeId}
            onChange={e => setRepresentativeId(e.target.value)}
          >
            <option value="">Todos</option>
            {representatives.map(rep => (
              <option key={rep.id} value={rep.id}>
                {rep.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="origin">Origem</FormLabel>
          <FormInput
            id="origin"
            type="text"
            placeholder="Filtrar por origem"
            value={origin}
            onChange={e => setOrigin(e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="lead_group_id">Grupo</FormLabel>
          <FormSelect
            id="lead_group_id"
            value={leadGroupId}
            onChange={e => setLeadGroupId(e.target.value)}
          >
            <option value="">Todos</option>
            {leadGroups?.map(group => (
              <option key={group.id} value={group.id}>
                {group.description}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="has_email">Possui e-mail</FormLabel>
          <FormSelect id="has_email" value={hasEmail} onChange={e => setHasEmail(e.target.value)}>
            <option value="">Todos</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="has_phone">Possui telefone</FormLabel>
          <FormSelect id="has_phone" value={hasPhone} onChange={e => setHasPhone(e.target.value)}>
            <option value="">Todos</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="last_contact_from">Último contato de</FormLabel>
          <FormInput
            id="last_contact_from"
            type="date"
            value={lastContactFrom}
            onChange={e => setLastContactFrom(e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="last_contact_to">Último contato até</FormLabel>
          <FormInput
            id="last_contact_to"
            type="date"
            value={lastContactTo}
            onChange={e => setLastContactTo(e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="type">Tipo de Lead</FormLabel>
          <FormSelect id="type" value={type} onChange={e => setType(e.target.value)}>
            <option value="">Todos</option>
            <option value="administrative">Administrativo</option>
            <option value="registration">Cadastro</option>
            <option value="commercial">Comercial</option>
            <option value="financial">Financeiro</option>
            <option value="legal_representative">Representante Legal</option>
            <option value="technical">Técnico</option>
            <option value="other">Outro</option>
          </FormSelect>
        </FormField>
      </div>

      <div className="mt-4 flex gap-2">
        <Button type="submit" className="flex-1">
          <Search className="h-4 w-4 mr-2" />
          Filtrar
        </Button>
        <Button type="button" variant="secondary" onClick={handleClear}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </form>
  );
}

export default LeadFilters;
